import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Users, Briefcase, Compass, GraduationCap, Rocket, LineChart,
  Sparkles, ShieldCheck, Crown, LogIn, UserPlus,
} from "lucide-react";
import { useAuth, ROLES, ROLE_LABELS } from "../context/AuthContext";
import SeoHead from "../components/SeoHead";

const ROLE_CARDS = [
  { role: ROLES.TALENT, icon: Users, tint: "var(--mod-talent)", descKey: "talent" },
  { role: ROLES.EMPLOYEUR, icon: Briefcase, tint: "var(--mod-emploi)", descKey: "employeur" },
  { role: ROLES.FREELANCE, icon: Compass, tint: "var(--mod-freelance)", descKey: "freelance" },
  { role: ROLES.UNIVERSITE, icon: GraduationCap, tint: "var(--mod-formation)", descKey: "universite" },
  { role: ROLES.ENTREPRENEUR, icon: Rocket, tint: "var(--mod-entrepreneur)", descKey: "entrepreneur" },
  { role: ROLES.INVESTISSEUR, icon: LineChart, tint: "var(--mod-investissement)", descKey: "investisseur" },
  { role: ROLES.COACH, icon: Sparkles, tint: "var(--mod-coach-ai)", descKey: "coach" },
  { role: ROLES.ADMIN, icon: ShieldCheck, tint: "var(--mod-admin)", descKey: "admin" },
  { role: ROLES.SUPER_ADMIN, icon: Crown, tint: "var(--mod-admin)", descKey: "super-admin" },
];

export default function Login() {
  const { t } = useTranslation();
  const { login, register, formatError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "";
  const isSignupParam = params.get("mode") === "signup";
  const roleParam = params.get("role");
  const initialRole =
    roleParam && Object.values(ROLES).includes(roleParam) ? roleParam : ROLES.TALENT;

  const [mode, setMode] = useState(isSignupParam ? "signup" : "login");
  const [selectedRole, setSelectedRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isSignup = mode === "signup";
  const title = isSignup ? "Créer un compte AFRICTAL" : "Connexion AFRICTAL";
  const subtitle = isSignup
    ? "Sélectionnez votre profil puis créez votre Passeport Numérique."
    : "Ravi de vous revoir. Entrez vos identifiants pour continuer.";

  const redirectAfterAuth = (user) => {
    if (nextUrl) {
      navigate(nextUrl, { replace: true });
      return;
    }
    if (user.role === "admin" || user.role === "super-admin") {
      navigate("/admin", { replace: true });
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let user;
      if (isSignup) {
        if (!name.trim()) throw new Error(t("login.err_name_required"));
        if (password.length < 6) throw new Error(t("login.err_password_short"));
        user = await register({ name: name.trim(), email: email.trim().toLowerCase(), password, role: selectedRole });
      } else {
        user = await login({ email: email.trim().toLowerCase(), password });
      }
      redirectAfterAuth(user);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="afr-root" data-testid="afr-login-root">
      <SeoHead
        title="Connexion"
        description="Rejoignez AFRICTAL, l'écosystème digital panafricain : accédez à votre espace Talent, Freelance, Employeur, Entrepreneur, Université ou Investisseur."
        path="/login"
        noIndex
      />      <nav className="afr-nav">
        <Link to="/" className="afr-nav__brand" data-testid="login-brand-home">
          <span className="afr-mark" aria-hidden="true">
            <img src="/brand/africtal-logo.png" alt="AFRICTAL" />
          </span>
          <span className="afr-brand-text">AFRICTAL</span>
          <span className="afr-tag">{t("nav.brand_tag")}</span>
        </Link>
        <div className="afr-nav__links">
          <Link to="/" data-testid="login-back-home">{t("login.back_home")}</Link>
        </div>
      </nav>

      <section className="afr-section" data-testid="afr-login-section">
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <span className="afr-eyebrow">{isSignup ? t("login.eyebrow_signup") : t("login.eyebrow_login")}</span>
          <h1 className="afr-hero__title" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>{title}</h1>
          <p className="afr-hero__lead" style={{ fontSize: "1.05rem" }}>{subtitle}</p>

          {/* Tab switcher */}
          <div
            className="afr-flex-row"
            style={{
              gap: 0,
              background: "var(--afr-elev)",
              padding: "0.3rem",
              borderRadius: "999px",
              width: "fit-content",
              marginBottom: "1.6rem",
            }}
            data-testid="login-mode-tabs"
          >
            <button
              type="button"
              className={`afr-btn ${mode === "login" ? "afr-btn--primary" : "afr-btn--ghost"} afr-btn--sm`}
              onClick={() => { setMode("login"); setError(""); }}
              data-testid="tab-login"
            >
              <LogIn size={14} /> {t("login.tab_login")}
            </button>
            <button
              type="button"
              className={`afr-btn ${mode === "signup" ? "afr-btn--primary" : "afr-btn--ghost"} afr-btn--sm`}
              onClick={() => { setMode("signup"); setError(""); }}
              data-testid="tab-signup"
            >
              <UserPlus size={14} /> {t("login.tab_signup")}
            </button>
          </div>

          <form onSubmit={submit} data-testid="afr-login-form">
            {isSignup && (
              <>
                <h3 style={{ fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", marginBottom: "1rem" }}>
                  {t("login.step_role")}
                </h3>
                <div
                  className="afr-grid"
                  style={{ gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", marginBottom: "2rem" }}
                  data-testid="afr-login-roles"
                >
                  {ROLE_CARDS.map(({ role, icon: Icon, tint, descKey }) => {
                    const active = selectedRole === role;
                    return (
                      <button
                        type="button"
                        key={role}
                        onClick={() => setSelectedRole(role)}
                        className="afr-persona"
                        data-testid={`login-role-${role}`}
                        aria-pressed={active}
                        style={{
                          textAlign: "left",
                          cursor: "pointer",
                          borderColor: active ? tint : "var(--afr-border)",
                          borderWidth: active ? "2px" : "1px",
                          background: active
                            ? `color-mix(in oklab, ${tint} 8%, var(--afr-surface))`
                            : "var(--afr-surface)",
                          boxShadow: active ? "0 8px 24px rgba(15,42,66,0.08)" : "none",
                          transform: active ? "translateY(-2px)" : "none",
                        }}
                      >
                        <div
                          className="afr-persona__icon"
                          style={{ background: `linear-gradient(135deg, ${tint}, var(--afr-accent))` }}
                        >
                          <Icon size={22} />
                        </div>
                        <h3>{ROLE_LABELS[role]}</h3>
                        <p>{t(`login.roles_desc.${descKey}`)}</p>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <h3 style={{ fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", marginBottom: "1rem" }}>
              {isSignup ? t("login.step_info_signup") : t("login.step_info_login")}
            </h3>
            <div className="afr-grid" style={{ gridTemplateColumns: isSignup ? "1fr 1fr" : "1fr", marginBottom: "1.4rem", gap: "1rem" }}>
              {isSignup && (
                <label className="afr-card" style={{ padding: "0.9rem" }}>
                  <span className="afr-muted" style={{ fontSize: "0.82rem" }}>{t("login.field_name")}</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("login.field_name_placeholder")}
                    required
                    data-testid="login-input-name"
                    style={{ width: "100%", marginTop: "0.4rem", border: "none", outline: "none", background: "transparent", fontFamily: "'Outfit',sans-serif", fontSize: "1.05rem", color: "var(--afr-secondary)" }}
                  />
                </label>
              )}
              <label className="afr-card" style={{ padding: "0.9rem" }}>
                <span className="afr-muted" style={{ fontSize: "0.82rem" }}>{t("login.field_email")}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.field_email_placeholder")}
                  required
                  data-testid="login-input-email"
                  style={{ width: "100%", marginTop: "0.4rem", border: "none", outline: "none", background: "transparent", fontFamily: "'Outfit',sans-serif", fontSize: "1.05rem", color: "var(--afr-secondary)" }}
                />
              </label>
            </div>
            <label className="afr-card" style={{ padding: "0.9rem", marginBottom: "0.4rem", display: "block" }}>
              <span className="afr-muted" style={{ fontSize: "0.82rem" }}>{t("login.field_password")}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isSignup ? t("login.field_password_placeholder_signup") : t("login.field_password_placeholder_login")}
                required
                minLength={6}
                data-testid="login-input-password"
                style={{ width: "100%", marginTop: "0.4rem", border: "none", outline: "none", background: "transparent", fontFamily: "'Outfit',sans-serif", fontSize: "1.05rem", color: "var(--afr-secondary)" }}
              />
            </label>
            {!isSignup && (
              <div style={{ textAlign: "right", marginBottom: "1.2rem" }}>
                <Link to="/forgot-password" data-testid="login-forgot-link" style={{ fontSize: "0.85rem", color: "var(--afr-primary)" }}>
                  {t("login.forgot")}
                </Link>
              </div>
            )}

            {error && (
              <div
                className="afr-card"
                style={{ background: "color-mix(in oklab, var(--afr-danger) 8%, transparent)", borderColor: "color-mix(in oklab, var(--afr-danger) 30%, var(--afr-border))", color: "var(--afr-danger)", marginBottom: "1.2rem" }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <div className="afr-flex-row">
              <button
                type="submit"
                className="afr-btn afr-btn--primary afr-btn--lg"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <><span className="afr-spinner" /> {t("login.processing")}</>
                ) : isSignup ? (
                  <><UserPlus size={16} /> {t("login.submit_signup")} · {ROLE_LABELS[selectedRole]}</>
                ) : (
                  <><LogIn size={16} /> {t("login.submit_login")}</>
                )}
              </button>
              <Link to="/" className="afr-btn afr-btn--outline afr-btn--lg" data-testid="login-cancel">
                {t("login.cancel")}
              </Link>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
